/**
 * Agenda View — Mobile-first chronological list, grouped by day.
 *
 * Props:
 *   appointments
 *   staff
 *   services
 *   rangeStart: Date — start day (inclusive)
 *   rangeDays:  number — how many days to show (default 7)
 *   onClickAppointment(appt)
 *   onClickSlot(day) — adds new appt for that day
 *   language
 *   filterServiceId
 *   onFilterServiceChange
 *   quickRange: 'today'|'tomorrow'|'week'|'month'
 *   onQuickRangeChange(range)
 */
import React, { useMemo } from "react";
import {
  format, parseISO, isSameDay, isToday, isTomorrow, addDays, isWithinInterval, startOfDay, endOfDay,
} from "date-fns";
import { it as itLocale, enUS } from "date-fns/locale";
import { Filter, X, Clock, User, Briefcase, Plus, AlertTriangle, CalendarDays, Ban } from "lucide-react";

const STATUS_TONE = {
  scheduled: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900/40",
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900/40",
  pending:   "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900/40",
  awaiting_confirmation: "bg-orange-50 text-orange-700 border-orange-200",
  completed: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/30 dark:text-violet-300 dark:border-violet-900/40",
  cancelled: "bg-rose-50 text-rose-700 border-rose-200 line-through opacity-70",
  no_show:   "bg-slate-100 text-slate-600 border-slate-200",
};

const STATUS_LABEL = {
  it: {
    scheduled: "Programmato", confirmed: "Confermato", pending: "In attesa",
    awaiting_confirmation: "Attende conferma", completed: "Completato",
    cancelled: "Annullato", no_show: "No-show", blocked: "Bloccato",
  },
  en: {
    scheduled: "Scheduled", confirmed: "Confirmed", pending: "Pending",
    awaiting_confirmation: "Awaiting confirmation", completed: "Completed",
    cancelled: "Cancelled", no_show: "No-show", blocked: "Blocked",
  },
};

const QUICK_RANGES = [
  { key: "today",    it: "Oggi",       en: "Today" },
  { key: "tomorrow", it: "Domani",     en: "Tomorrow" },
  { key: "week",     it: "Settimana",  en: "Week" },
  { key: "month",    it: "Mese",       en: "Month" },
];

const AgendaView = ({
  appointments = [],
  staff = [],
  services = [],
  rangeStart = new Date(),
  rangeDays = 7,
  onClickAppointment,
  onClickSlot,
  language = "it",
  filterServiceId = null,
  onFilterServiceChange,
  quickRange = "week",
  onQuickRangeChange,
}) => {
  const tenantHasServices = services && services.length > 0;
  const filterService = useMemo(
    () => services.find(s => s.service_id === filterServiceId),
    [services, filterServiceId]
  );

  const staffById = useMemo(() => {
    const m = {};
    staff.forEach(s => { m[s.staff_id] = s; });
    return m;
  }, [staff]);

  const rangeStartDay = useMemo(() => startOfDay(rangeStart), [rangeStart]);
  const rangeEndDay   = useMemo(() => endOfDay(addDays(rangeStartDay, rangeDays - 1)), [rangeStartDay, rangeDays]);

  const days = useMemo(() => Array.from({ length: rangeDays }, (_, i) => addDays(rangeStartDay, i)),
    [rangeStartDay, rangeDays]);

  // Filter + sort + group by day
  const byDay = useMemo(() => {
    const list = (appointments || []).filter(a => {
      if (filterServiceId) {
        const ids = a.service_ids || (a.service_id ? [a.service_id] : []);
        if (ids.length > 0 && !ids.includes(filterServiceId)) return false;
      }
      if (!a.date) return false;
      const d = parseISO(a.date);
      return isWithinInterval(d, { start: rangeStartDay, end: rangeEndDay });
    });
    list.sort((a, b) => {
      const da = `${a.date} ${a.start_time || a.time || "00:00"}`;
      const db = `${b.date} ${b.start_time || b.time || "00:00"}`;
      return da.localeCompare(db);
    });
    const m = {};
    days.forEach(d => { m[format(d, "yyyy-MM-dd")] = []; });
    list.forEach(a => {
      const key = (a.date || "").slice(0, 10);
      if (m[key]) m[key].push(a);
    });
    return m;
  }, [appointments, filterServiceId, days, rangeStartDay, rangeEndDay]);

  const dayLabel = (d) => {
    if (isToday(d))    return language === "it" ? "Oggi"   : "Today";
    if (isTomorrow(d)) return language === "it" ? "Domani" : "Tomorrow";
    return format(d, "EEEE", { locale: language === "it" ? itLocale : enUS });
  };

  const totalCount = useMemo(() =>
    Object.values(byDay).reduce((acc, arr) => acc + arr.length, 0),
    [byDay]
  );

  return (
    <div className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 overflow-hidden shadow-sm" data-testid="agenda-view">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-b border-slate-100 dark:border-white/5 bg-gradient-to-r from-slate-50 to-white dark:from-slate-900/60 dark:to-slate-900/30">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Agenda</p>
          <p className="text-sm font-bold text-slate-800 dark:text-slate-200">
            {totalCount} {language === "it" ? "appuntamenti" : "appointments"}
            <span className="ml-2 text-slate-400 font-normal">
              · {format(rangeStartDay, "d MMM", { locale: language === "it" ? itLocale : enUS })}
              {rangeDays > 1 && ` – ${format(rangeEndDay, "d MMM", { locale: language === "it" ? itLocale : enUS })}`}
            </span>
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Quick range pills */}
          {onQuickRangeChange && (
            <div className="inline-flex p-0.5 rounded-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10" data-testid="agenda-quick-range">
              {QUICK_RANGES.map(r => (
                <button key={r.key}
                  onClick={() => onQuickRangeChange(r.key)}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded-full transition-all ${
                    quickRange === r.key
                      ? "bg-white dark:bg-slate-900 shadow text-blue-600 dark:text-blue-300"
                      : "text-slate-500 hover:bg-white/60"
                  }`}
                  data-testid={`agenda-range-${r.key}`}
                >
                  {language === "it" ? r.it : r.en}
                </button>
              ))}
            </div>
          )}

          {tenantHasServices && onFilterServiceChange && (
            <div className="flex items-center gap-1 rounded-full bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 px-2 py-1" data-testid="agenda-service-filter">
              <Filter className="w-3 h-3 text-slate-400" />
              <select
                className="text-[11px] font-bold bg-transparent outline-none text-slate-700 dark:text-slate-200 pr-1 max-w-[160px]"
                value={filterServiceId || ""}
                onChange={(e) => onFilterServiceChange(e.target.value || null)}
                data-testid="agenda-service-filter-select"
              >
                <option value="">{language === "it" ? "Tutti i servizi" : "All services"}</option>
                {services.map(s => (
                  <option key={s.service_id} value={s.service_id}>{s.name}</option>
                ))}
              </select>
              {filterService && (
                <button type="button" onClick={() => onFilterServiceChange(null)}
                  className="text-slate-400 hover:text-slate-600" title={language === "it" ? "Cancella filtro" : "Clear"}
                  data-testid="agenda-service-filter-clear">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      <div className="divide-y divide-slate-100 dark:divide-white/5">
        {days.map(day => {
          const dateStr = format(day, "yyyy-MM-dd");
          const items = byDay[dateStr] || [];
          const todayHi = isToday(day);
          return (
            <section key={dateStr} className={`px-4 py-4 ${todayHi ? "bg-gradient-to-r from-blue-50/40 to-transparent dark:from-blue-950/15" : ""}`} data-testid={`agenda-day-${dateStr}`}>
              <header className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-xl flex flex-col items-center justify-center shrink-0 ${todayHi ? "bg-gradient-to-br from-blue-500 to-violet-500 text-white" : "bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300"}`}>
                    <span className="text-[8px] uppercase tracking-wider font-bold leading-none">{format(day, "MMM", { locale: language === "it" ? itLocale : enUS })}</span>
                    <span className="text-xs font-black leading-none">{format(day, "d")}</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200 capitalize">{dayLabel(day)}</p>
                    <p className="text-[10px] text-slate-400 font-mono uppercase tracking-wider">
                      {items.length} {language === "it" ? "appt" : "appt"}
                    </p>
                  </div>
                </div>
                {onClickSlot && (
                  <button
                    onClick={() => onClickSlot(day, "09:00")}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30 px-2 py-1 rounded-full transition"
                    data-testid={`agenda-add-${dateStr}`}
                  >
                    <Plus className="w-3 h-3" /> {language === "it" ? "Aggiungi" : "Add"}
                  </button>
                )}
              </header>

              {items.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 dark:border-white/10 py-4 px-3 text-center" data-testid={`agenda-empty-${dateStr}`}>
                  <CalendarDays className="w-4 h-4 mx-auto text-slate-300 mb-1" />
                  <p className="text-[11px] text-slate-400">
                    {language === "it" ? "Nessun appuntamento" : "No appointments"}
                  </p>
                </div>
              ) : (
                <ul className="space-y-1.5">
                  {items.map(appt => {
                    const startTime = appt.start_time || appt.time;
                    const duration  = parseInt(appt.total_duration || appt.duration || 30, 10);
                    const member    = staffById[appt.staff_id];
                    const accent    = member?.color || "#6366f1";
                    const isBlock   = appt.is_blocked || appt.status === "blocked";
                    const statusKey = isBlock ? "blocked" : (appt.status || "scheduled");
                    const toneCls   = isBlock ? "bg-slate-100 text-slate-700 border-slate-200" : (STATUS_TONE[appt.status] || STATUS_TONE.scheduled);
                    const statusLbl = STATUS_LABEL[language]?.[statusKey] || statusKey;
                    return (
                      <li key={appt.appointment_id}>
                        <button
                          onClick={() => onClickAppointment && onClickAppointment(appt)}
                          className="w-full flex items-stretch gap-3 rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/40 px-3 py-2.5 text-left hover:shadow-md hover:-translate-y-px transition-all group"
                          data-testid={`agenda-appt-${appt.appointment_id}`}
                        >
                          <div className="w-1 rounded-full shrink-0 self-stretch" style={{ background: accent }} />
                          <div className="w-14 shrink-0 text-center">
                            <p className="text-base font-black text-slate-800 dark:text-slate-100 font-mono leading-tight">{startTime}</p>
                            <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">{duration}m</p>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate flex items-center gap-1.5">
                              {isBlock && <Ban className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                              {appt.client_name || (isBlock ? (appt.reason || statusLbl) : "—")}
                            </p>
                            <p className="text-[11px] text-slate-500 truncate flex items-center gap-3 mt-0.5">
                              {appt.service_name && (
                                <span className="inline-flex items-center gap-1 truncate">
                                  <Briefcase className="w-3 h-3" /> {appt.service_name}
                                </span>
                              )}
                              {member?.name && (
                                <span className="inline-flex items-center gap-1 truncate">
                                  <User className="w-3 h-3" /> {member.name}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex flex-col items-end justify-between shrink-0">
                            <span className={`inline-flex items-center text-[9px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded-full border ${toneCls}`}>
                              {statusLbl}
                            </span>
                            <span className="text-[9px] text-slate-400 mt-1">
                              {appt.client_phone || appt.client_email || ""}
                            </span>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default AgendaView;
